import { api } from './baseApi';
import { inventoryApi } from './inventoryApi';
import type { ApiResponse, Item, Customer, Transaction, TransactionFormData, SavePendingTransactionData } from '@/types';

const DEFAULT_PENDING_LIMIT = 25;
const MAX_PENDING_LIMIT = 200;

// Helper to populate etalase from itemOutletEtalases array
function transformItem(item: Item): Item {
  if (item.itemOutletEtalases && item.itemOutletEtalases.length > 0) {
    return {
      ...item,
      etalase: item.itemOutletEtalases[0].etalase,
    };
  }
  return item;
}

export const posApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get all categories for POS
    getCategories: builder.query<
      ApiResponse<Array<{
        id: string;
        name: string;
        isActive: boolean;
        subcategories?: Array<{
          id: string;
          name: string;
          isActive: boolean;
        }>;
        _count: { items: number; subcategories: number };
      }>>,
      { storeId?: string } | void
    >({
      query: (params) => {
        const storeId = params && 'storeId' in params ? params.storeId : undefined;
        return {
          url: '/categories',
          params: {
            isActive: true,
            includeSubcategories: true,
            ...(storeId && { storeId }),
          },
        };
      },
      providesTags: ['Category'],
    }),

    // Get items by category for POS
    getItemsByCategory: builder.query<
      ApiResponse<{
        items: Array<Item>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>,
      { categoryId?: string; subcategoryId?: string; search?: string; limit?: number; page?: number; outletId?: string; storeId?: string }
    >({
      query: ({ categoryId, subcategoryId, search, limit = 20, page = 1, outletId, storeId }) => ({
        url: '/items',
        params: {
          ...(categoryId && { categoryId }),
          ...(subcategoryId && { subcategoryId }),
          ...(search && { search }),
          ...(outletId && { outletId }),
          ...(storeId && { storeId }),
          limit,
          page,
          isActive: true,
        },
      }),
      transformResponse: (response: ApiResponse<{
        items: Array<Item>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>) => {
        if (response.data?.items) {
          return {
            ...response,
            data: {
              ...response.data,
              items: response.data.items.map(transformItem),
            },
          };
        }
        return response;
      },
      providesTags: ['Item'],
    }),

    // Search items by barcode
    getItemByBarcode: builder.query<
      ApiResponse<Item>,
      string
    >({
      query: (barcode) => `/items/barcode/${barcode}`,
      transformResponse: (response: ApiResponse<Item>) => {
        if (response.data) {
          return {
            ...response,
            data: transformItem(response.data),
          };
        }
        return response;
      },
      providesTags: ['Item'],
    }),

    // Search customers for POS
    searchCustomers: builder.query<
      ApiResponse<Array<Customer>>,
      { search: string; storeId?: string }
    >({
      query: ({ search, storeId }) => ({
        url: '/customers',
        params: {
          search,
          limit: 10,
          ...(storeId && { storeId }),
        },
      }),
      providesTags: ['Customer'],
    }),

    // Create new customer quickly
    createCustomer: builder.mutation<
      ApiResponse<Customer>,
      { name: string; phoneNumber?: string; email?: string }
    >({
      query: (customerData) => ({
        url: '/customers',
        method: 'POST',
        body: customerData,
      }),
      invalidatesTags: ['Customer'],
    }),

    // Create transaction
    createTransaction: builder.mutation<
      ApiResponse<Transaction>,
      TransactionFormData
    >({
      query: (transactionData) => ({
        url: '/transactions',
        method: 'POST',
        body: transactionData,
      }),
      invalidatesTags: ['Transaction', 'Item', 'ItemStock', 'Report', 'CashDrawer', 'StoreCash', 'Customer'],
      // Force refetch of pending transactions after completing transaction
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate and refetch pending transactions
          dispatch(posApi.util.invalidateTags(['Transaction']));
          // Cross-slice invalidation for inventory data
          dispatch(inventoryApi.util.invalidateTags(['Item', 'ItemStock', 'InventoryStats', 'StockHistory']));
        } catch {
          // Handle error if needed
        }
      },
    }),


    // Save transaction as pending
    saveTransaction: builder.mutation<
      ApiResponse<Transaction>,
      SavePendingTransactionData
    >({
      query: (transactionData) => ({
        url: '/transactions/save',
        method: 'POST',
        body: {
          ...transactionData,
          status: 'PENDING',
        },
      }),
      invalidatesTags: ['Transaction', 'Item', 'ItemStock', 'InventoryStats'],
      // Force refetch of pending transactions after saving
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate and refetch pending transactions and inventory
          dispatch(posApi.util.invalidateTags(['Transaction']));
          dispatch(inventoryApi.util.invalidateTags(['Item', 'ItemStock', 'InventoryStats']));
        } catch {
          // Handle error if needed
        }
      },
    }),

    // Update pending transaction
    updatePendingTransaction: builder.mutation<
      ApiResponse<Transaction>,
      { id: string; data: Omit<TransactionFormData, 'paymentMethod' | 'amountPaid'> }
    >({
      query: ({ id, data }) => ({
        url: `/transactions/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Transaction', 'Item', 'ItemStock', 'InventoryStats'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(posApi.util.invalidateTags(['Transaction']));
          // Cross-slice invalidation for inventory data
          dispatch(inventoryApi.util.invalidateTags(['Item', 'ItemStock', 'InventoryStats']));
        } catch {
          // Handle error if needed
        }
      },
    }),

    // Complete pending transaction (convert to completed with payment)
    completePendingTransaction: builder.mutation<
      ApiResponse<Transaction>,
      { id: string; data: TransactionFormData }
    >({
      query: ({ id, data }) => ({
        url: `/transactions/${id}/complete`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Transaction', 'Item', 'ItemStock', 'Report', 'CashDrawer', 'StoreCash'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(posApi.util.invalidateTags(['Transaction']));
          dispatch(inventoryApi.util.invalidateTags(['Item', 'ItemStock', 'InventoryStats', 'StockHistory']));
        } catch {
          // Handle error if needed
        }
      },
    }),

    // Get pending transactions
    getPendingTransactions: builder.query<
      ApiResponse<{
        transactions: Array<Transaction>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>,
      { page?: number; limit?: number; outletId?: string } | void
    >({
      query: (params) => {
        const queryParams = params || {};
        const rawPage = Number(queryParams.page);
        const safePage = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
        const rawLimit = Number(queryParams.limit);
        const safeLimit = Math.min(
          Math.max(Number.isFinite(rawLimit) && rawLimit > 0 ? rawLimit : DEFAULT_PENDING_LIMIT, 1),
          MAX_PENDING_LIMIT,
        );
        const outletId = queryParams.outletId;

        return {
          url: '/transactions',
          params: {
            status: 'PENDING',
            page: safePage,
            limit: safeLimit,
            ...(outletId && { outletId }),
          },
        };
      },
      providesTags: ['Transaction'],
      // Force refetch on mount to ensure fresh data
      keepUnusedDataFor: 0,
    }),

    // Continue pending transaction
    continueTransaction: builder.query<
      ApiResponse<Transaction>,
      string
    >({
      query: (transactionId) => `/transactions/${transactionId}`,
      providesTags: ['Transaction'],
    }),

    // Delete pending transaction
    deletePendingTransaction: builder.mutation<
      ApiResponse<{ id: string; transactionCode: string }>,
      string
    >({
      query: (transactionId) => ({
        url: `/transactions/${transactionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Transaction'],
      // Force refetch of pending transactions after deletion
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate and refetch pending transactions and inventory
          dispatch(posApi.util.invalidateTags(['Transaction']));
          dispatch(inventoryApi.util.invalidateTags(['Item', 'ItemStock', 'InventoryStats']));
        } catch {
          // Handle error if needed
        }
      },
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetItemsByCategoryQuery,
  useGetItemByBarcodeQuery,
  useSearchCustomersQuery,
  useCreateCustomerMutation,
  useCreateTransactionMutation,
  useSaveTransactionMutation,
  useUpdatePendingTransactionMutation,
  useCompletePendingTransactionMutation,
  useGetPendingTransactionsQuery,
  useContinueTransactionQuery,
  useDeletePendingTransactionMutation,
} = posApi;
